import React, { useState, useEffect } from "react";
import { useAdminGetUserCompletedCoursesQuery } from "../../../services/organization/adminUsers";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "../../LoadingIndicator";

function CertificateMedia({ src, alt, className = "" }) {
  const { t } = useTranslation("common");
  const [hasError, setHasError] = useState(!src);

  useEffect(() => {
    setHasError(!src);
  }, [src]);

  if (hasError) {
    return (
      <div
        className={`admin-employee-profile__cert-placeholder ${className}`.trim()}
        role="img"
        aria-label={t("certificateLoadFailed")}
      >
        <span
          className="fas fa-certificate admin-employee-profile__cert-placeholder-icon"
          aria-hidden="true"
        />
        <p className="admin-employee-profile__cert-placeholder-text mb-0">
          {t("certificateLoadFailed")}
        </p>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className={`admin-employee-profile__cert-image ${className}`.trim()}
      onError={() => setHasError(true)}
    />
  );
}

const Cert = ({ user }) => {
  const { data: completedCourses, isLoading: isCompletedCoursesLoading } =
    useAdminGetUserCompletedCoursesQuery(user?.id);
  const { t } = useTranslation("common");

  const [showPreview, setShowPreview] = useState(false);
  const [currentImage, setCurrentImage] = useState("");
  const [currentTitle, setCurrentTitle] = useState("");

  const onClose = () => {
    setShowPreview(false);
    setCurrentImage("");
    setCurrentTitle("");
  };

  if (isCompletedCoursesLoading) return <LoadingIndicator />;

  const certificates = completedCourses?.data ?? [];

  return (
    <div className="profile-about admin-employee-profile__certs">
      <div className="row">
        <div className="col-12">
          <h1 className="font_20 font-extrabold mb-1">{t("certificates")}</h1>
          <p className="font_14 font-color-grey mb-4">
            {completedCourses?.pagination?.count ?? 0}{" "}
            {t("completedCoursesCount")}
          </p>
        </div>
      </div>

      <div className="row g-3 admin-employee-profile__certs-grid">
        {certificates.length ? (
          certificates.map((completedCourse) => {
            const certificateUrl =
              completedCourse?.enrollment?.certificate_url || "";
            const title =
              completedCourse?.title ||
              completedCourse?.name ||
              t("certificate");

            return (
              <div
                className="col-12 col-sm-6 col-lg-4"
                key={completedCourse?.enrollment?.id ?? completedCourse?.id}
              >
                <button
                  type="button"
                  className="show-image admin-employee-profile__cert-card"
                  onClick={() => {
                    setShowPreview(true);
                    setCurrentImage(certificateUrl);
                    setCurrentTitle(title);
                  }}
                >
                  <div className="show-image__text">
                    <i className="far fa-eye font_22" aria-hidden="true" />
                    <p className="font_12 font-extrabold mb-0">
                      {t("showCertificate")}
                    </p>
                  </div>
                  <CertificateMedia
                    src={certificateUrl}
                    alt={title}
                  />
                </button>
                <p className="admin-employee-profile__cert-title font_14 font-bold mb-0">
                  {title}
                </p>
              </div>
            );
          })
        ) : (
          <div className="col-12">
            <p className="admin-employee-profile__empty-state">
              {t("emptyStates.certificates")}
            </p>
          </div>
        )}
      </div>

      <div
        className={`preview-image-container ${
          showPreview ? "preview-image-container--show" : ""
        }`}
        onClick={onClose}
      >
        <div
          className="preview-image-container__content"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="d-flex justify-content-between mb-3 align-items-center gap-2">
            <h4 className="font_18 font-bold mb-0">
              {currentTitle || t("certificate")}
            </h4>
            <button
              type="button"
              className="btn btn-main-color"
              onClick={onClose}
              aria-label={t("cancel")}
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          </div>
          <CertificateMedia
            src={currentImage}
            alt={currentTitle || t("certificate")}
            className="admin-employee-profile__cert-preview"
          />
        </div>
      </div>
    </div>
  );
};

export default Cert;
